import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

export const PERMISSION_STATE = Object.freeze({
  GRANTED: "granted",
  DENIED: "denied",
  PROMPT: "prompt",
  UNSUPPORTED: "unsupported",
});

const isBrowser = () => typeof window !== "undefined" && typeof navigator !== "undefined";

export function getNotificationPermissionState() {
  if (!isBrowser() || !("Notification" in window)) {
    return PERMISSION_STATE.UNSUPPORTED;
  }

  if (Notification.permission === "default") return PERMISSION_STATE.PROMPT;
  return Notification.permission;
}

export async function ensureNotificationPermission({ request = false } = {}) {
  const current = getNotificationPermissionState();

  if (current === PERMISSION_STATE.GRANTED || current === PERMISSION_STATE.DENIED) {
    return current;
  }

  if (current === PERMISSION_STATE.UNSUPPORTED || !request) {
    return current;
  }

  // Call with request:true only from a direct user action (button/tap).
  const next = await Notification.requestPermission();
  return next === "default" ? PERMISSION_STATE.PROMPT : next;
}

export async function getGeolocationPermissionState() {
  if (Capacitor.isNativePlatform()) {
    try {
      const permissions = await Geolocation.checkPermissions();
      return permissions.location === "granted" || permissions.coarseLocation === "granted"
        ? PERMISSION_STATE.GRANTED
        : permissions.location;
    } catch (_) {
      return PERMISSION_STATE.PROMPT;
    }
  }
  if (!isBrowser() || !("geolocation" in navigator)) {
    return PERMISSION_STATE.UNSUPPORTED;
  }

  if (navigator.permissions?.query) {
    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      return status.state;
    } catch (_) {
      // Some browsers implement geolocation but do not expose it through Permissions API.
    }
  }

  return PERMISSION_STATE.PROMPT;
}

const createLocationError = (code, message, originalError) => {
  const error = new Error(message);
  error.code = code;
  if (originalError) error.originalError = originalError;
  return error;
};

const normalizeGeolocationError = (error) => {
  if (!error) {
    return createLocationError("LOCATION_UNKNOWN_ERROR", "خطای نامشخص در دریافت موقعیت مکانی");
  }

  if (error.code === 1) {
    return createLocationError("LOCATION_PERMISSION_DENIED", "دسترسی به موقعیت مکانی داده نشد", error);
  }

  if (error.code === 2) {
    return createLocationError("LOCATION_UNAVAILABLE", "موقعیت مکانی در دسترس نیست", error);
  }

  if (error.code === 3) {
    return createLocationError("LOCATION_TIMEOUT", "دریافت موقعیت مکانی بیش از حد طول کشید", error);
  }

  return createLocationError("LOCATION_UNKNOWN_ERROR", error.message || "خطا در دریافت موقعیت مکانی", error);
};

export async function getCurrentPositionWithPermission(options = {}) {
  if (Capacitor.isNativePlatform()) {
    try {
      let permission = await Geolocation.checkPermissions();
      if (permission.location !== "granted" && permission.coarseLocation !== "granted") {
        permission = await Geolocation.requestPermissions();
      }
      if (permission.location !== "granted" && permission.coarseLocation !== "granted") {
        throw createLocationError("LOCATION_PERMISSION_DENIED", "دسترسی به موقعیت مکانی داده نشد");
      }
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true, timeout: 15000, maximumAge: 30000, ...options,
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
        raw: position,
      };
    } catch (error) {
      if (error.code === "LOCATION_PERMISSION_DENIED") throw error;
      if (error.code === "OS-PLUG-GLOC-0003") {
        throw createLocationError("LOCATION_PERMISSION_DENIED", "دسترسی به موقعیت مکانی داده نشد", error);
      }
      if (error.code === "OS-PLUG-GLOC-0007") {
        throw createLocationError("LOCATION_UNAVAILABLE", "موقعیت مکانی دستگاه خاموش است", error);
      }
      if (error.code === "OS-PLUG-GLOC-0010") {
        throw createLocationError("LOCATION_TIMEOUT", "دریافت موقعیت مکانی بیش از حد طول کشید", error);
      }
      throw createLocationError("LOCATION_UNAVAILABLE", error.message || "دریافت موقعیت مکانی ممکن نشد", error);
    }
  }
  if (!isBrowser() || !("geolocation" in navigator)) {
    throw createLocationError("LOCATION_UNSUPPORTED", "این دستگاه از موقعیت مکانی پشتیبانی نمی‌کند");
  }

  if (!window.isSecureContext && window.location.hostname !== "localhost") {
    throw createLocationError("LOCATION_REQUIRES_HTTPS", "دریافت موقعیت مکانی فقط روی HTTPS امکان‌پذیر است");
  }

  const state = await getGeolocationPermissionState();
  if (state === PERMISSION_STATE.DENIED) {
    throw createLocationError("LOCATION_PERMISSION_DENIED", "دسترسی به موقعیت مکانی قبلاً رد شده است");
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp,
          raw: position,
        });
      },
      (error) => reject(normalizeGeolocationError(error)),
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 30000,
        ...options,
      }
    );
  });
}

export function watchPositionWithPermission(onPosition, onError, options = {}) {
  if (!isBrowser() || !("geolocation" in navigator)) {
    onError?.({ code: "LOCATION_UNSUPPORTED", message: "این دستگاه از موقعیت مکانی پشتیبانی نمی‌کند" });
    return null;
  }

  return navigator.geolocation.watchPosition(
    (position) => {
      onPosition?.({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
        raw: position,
      });
    },
    (error) => onError?.(normalizeGeolocationError(error)),
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 10000,
      ...options,
    }
  );
}

export function clearPositionWatch(watchId) {
  if (watchId == null || !isBrowser() || !("geolocation" in navigator)) return;
  navigator.geolocation.clearWatch(watchId);
}
